import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, Bot, Code2, Globe2, ShieldCheck, Sparkles, Zap } from 'lucide-react'
import gsap from 'gsap'

const capabilities = [
  { label: 'Conversion Websites', icon: Globe2 },
  { label: 'SaaS Products', icon: Code2 },
  { label: 'AI Agents & Automation', icon: Bot },
]

export default function Hero() {
  const root = useRef<HTMLElement>(null)

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo('.hero-reveal', { y: 35, opacity: 0 }, { y: 0, opacity: 1, duration: .75, stagger: .08, ease: 'power3.out' })
    }, root)
    return () => ctx.revert()
  }, [])

  return (
    <section ref={root} className="relative overflow-hidden px-6 pb-20 pt-28 lg:px-10 lg:pb-28 lg:pt-36">
      <div className="pointer-events-none absolute left-1/2 top-0 h-[650px] w-[650px] -translate-x-1/2 rounded-full bg-[#B7000E]/10 blur-[140px]" />
      <div className="relative mx-auto grid max-w-[1500px] items-center gap-12 lg:grid-cols-[1.15fr_.85fr]">
        <div>
          <div className="hero-reveal mb-7 flex flex-wrap gap-2">
            {capabilities.map(({ label, icon: Icon }) => (
              <span key={label} className="flex items-center gap-2 rounded-full border border-white/10 bg-white/[.04] px-4 py-2 text-xs text-white/70">
                <Icon className="h-3.5 w-3.5 text-[#E30614]" />{label}
              </span>
            ))}
          </div>
          <p className="hero-reveal text-mono mb-5 text-[#E30614]">US-REGISTERED DIGITAL PRODUCT & GROWTH PARTNER</p>
          <h1 className="hero-reveal text-display max-w-5xl uppercase">
            Build. Automate.<br/><span className="text-[#B7000E]">Grow.</span>
          </h1>
          <p className="hero-reveal mt-8 max-w-3xl text-lg leading-relaxed text-white/72 lg:text-xl">
            Technifire builds conversion-focused websites, scalable SaaS products and AI-powered systems that help businesses generate leads, reduce manual work and launch faster.
          </p>
          <div className="hero-reveal mt-9 flex flex-col gap-3 sm:flex-row">
            <Link to="/contacts" className="btn-primary"><Zap className="h-4 w-4"/>BOOK A FREE STRATEGY CALL<ArrowRight className="h-4 w-4"/></Link>
            <Link to="/portfolio" className="btn-outline">VIEW CASE STUDIES</Link>
          </div>
          <div className="hero-reveal mt-9 grid max-w-3xl grid-cols-2 gap-5 border-t border-white/10 pt-7 md:grid-cols-4">
            {[
              ['150+','Projects delivered'],['98%','Client satisfaction'],['6+','Years experience'],['24h','Initial response']
            ].map(([value,label]) => <div key={label}><div className="text-2xl font-light lg:text-3xl">{value}</div><div className="mt-1 text-xs text-white/50">{label}</div></div>)}
          </div>
        </div>

        <div className="hero-reveal relative">
          <div className="overflow-hidden rounded-[28px] border border-white/10 bg-[#0A0A0A] shadow-2xl">
            <video className="aspect-[4/3] h-full w-full object-cover" autoPlay muted loop playsInline>
              <source src="/images/video-banner-2.mp4" type="video/mp4" />
            </video>
          </div>
          <div className="absolute -bottom-6 -left-5 max-w-[280px] rounded-2xl border border-white/10 bg-[#090909]/95 p-5 backdrop-blur-xl">
            <div className="mb-3 flex items-center gap-2 text-sm font-medium"><Sparkles className="h-4 w-4 text-[#E30614]"/>Business-first engineering</div>
            <p className="text-sm leading-relaxed text-white/55">Strategy, design, development, integrations and post-launch optimization through one accountable team.</p>
          </div>
          <div className="absolute -right-3 top-8 rounded-full border border-white/10 bg-black/85 px-4 py-2 text-xs text-white/70"><ShieldCheck className="mr-2 inline h-3.5 w-3.5 text-green-400"/>NDA & milestone delivery</div>
        </div>
      </div>
    </section>
  )
}
